import React from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/react';
import App from './App';
import EmbedChart from './pages/EmbedChart';
import PublicSharePage from './pages/PublicSharePage';
import './index.css';
import './styles/LightMode.css';
import { API_BASE_URL } from './config';
import './styles/brandTheme.css';
import { applyBrandTheme, applyBrandLayout } from './utils/brandTheme';
import './styles/brandLayouts.css';
import './styles/brandMono.css';
import { getActiveEntityBrand } from './utils/activeEntity';
import { applyBrandIdentity, applyEntryBrandLink } from './utils/brandIdentity';
import { installLightModeColors } from './utils/lightModeColors';

// /embed is the public iframe widget: render ONLY the chart component —
// no App shell, no auth/session hooks, no analytics, no navigation chrome.
const isEmbedRoute = /^\/embed\/?$/.test(window.location.pathname);
// Public results links (/s/<token>): standalone like the embed, no sign-in or brand theme.
const isShareRoute = /^\/s\/[A-Za-z0-9_-]+\/?$/.test(window.location.pathname);

// Paint the cached brand's colors before React renders, so a themed brand
// doesn't flash RiskLo purple first. EntitySwitcher re-applies the live theme.
if (!isEmbedRoute && !isShareRoute) {
  applyBrandTheme(getActiveEntityBrand()?.branding?.theme || null);
  applyBrandLayout(getActiveEntityBrand()?.branding?.layout || null);
}

// Light mode: darker gain / loss text than the dark-mode greens and reds.
if (!isEmbedRoute && !isShareRoute) installLightModeColors();

// The brand's name and icon in the browser tab, and a company's sign-in link
// (risklo.io/b/<slug>) picking that company before anyone signs in.
if (!isEmbedRoute && !isShareRoute) {
  applyBrandIdentity();
  applyEntryBrandLink().then(() => applyBrandIdentity());
}

// Create React App has no import.meta.env, so the key must be passed
// explicitly (Clerk's Vite quickstart reads VITE_* automatically — that does
// not apply here). Absent key = provider is skipped rather than crashing, so a
// dev box without Clerk configured still boots.
const clerkPublishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const container = document.getElementById('root');
const root = createRoot(container);

// /tutorial-login?token=… (ContentForge Tutorial Studio): sign this browser in
// as the demo account, then open the app. Anything else renders normally.
const isTutorialLogin = /^\/tutorial-login\/?$/.test(window.location.pathname);
if (isTutorialLogin) {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  fetch(`${API_BASE_URL}/api/auth/tutorial-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })
    .then((response) => response.json())
    .then((data) => {
      if (data?.success && data.token && data.user) {
        localStorage.setItem('risklo_auth_token', data.token);
        localStorage.setItem('risklo_user', JSON.stringify(data.user));
      }
    })
    .catch(() => undefined)
    .finally(() => window.location.replace('/'));
}

// The embed route is intentionally OUTSIDE ClerkProvider: it is a public,
// unauthenticated widget and must not load session machinery or call Clerk.
let tree;
if (isShareRoute) tree = <PublicSharePage />;
else tree = isEmbedRoute
  ? <EmbedChart />
  : (clerkPublishableKey
    ? <ClerkProvider publishableKey={clerkPublishableKey}><App /></ClerkProvider>
    : <App />);

if (!isTutorialLogin) {
  root.render(
    <React.StrictMode>
      {tree}
    </React.StrictMode>
  );
}
